import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

export interface PdfObjectUrl {
  /** A `blob:` URL an iframe or a tab can render; null until the bytes land. */
  url: string | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Any PDF this API serves, as an object URL to look at.
 *
 * The endpoints answer with `Content-Disposition: attachment` and are only
 * reachable with the session cookie `apiClient` sends, so there is no URL a
 * viewer could be handed directly: in production the SPA and the API are
 * separate origins, and a cross-origin top-level navigation would arrive
 * without the cookie. Fetching the bytes here and wrapping them in a blob is
 * what makes an in-app viewer possible without a new public route.
 *
 * Deliberately NOT a React Query hook. An object URL is a revocable handle,
 * not data: cached, it would outlive the `revokeObjectURL` below and hand a
 * later render a URL the browser has already forgotten.
 *
 * Pass null to fetch nothing — a closed viewer has no business asking the
 * server to render a PDF.
 *
 * Path-generic since NEW-238: it began as an invoice-only hook in
 * hooks/useInvoices.ts, and a send's archived copy needs the same machinery
 * against a different path.
 */
export function usePdfObjectUrl(path: string | null): PdfObjectUrl {
  const [state, setState] = useState<PdfObjectUrl>({
    url: null,
    isLoading: path !== null,
    isError: false,
  });

  useEffect(() => {
    if (!path) {
      setState({ url: null, isLoading: false, isError: false });
      return;
    }

    // Guards a response that lands after this PDF stopped being the one on
    // screen: without it, closing the viewer and opening another can paint the
    // first one's bytes, and leak the URL it created.
    let current = true;
    let created: string | null = null;

    setState({ url: null, isLoading: true, isError: false });

    void (async () => {
      try {
        const response = await apiClient.get<Response>(path);
        const blob = await response.blob();
        if (!current) return;
        created = URL.createObjectURL(blob);
        setState({ url: created, isLoading: false, isError: false });
      } catch {
        if (!current) return;
        setState({ url: null, isLoading: false, isError: true });
      }
    })();

    return () => {
      current = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [path]);

  return state;
}
